/**
 * Deterministic Question-Specific Coaching
 *
 * CHAT LOGIC (single source of truth for practice test coaching):
 * 1. Each question gets its own chat – no reuse.
 * 2. Two rounds max: Round 1 (reflection) → Round 2 (factual clarification).
 * 3. Card prompts: ≤6 words. Coach responses: short, factual where applicable.
 * 4. Ends with one take-home rule + move-on prompt.
 *
 * ROUND 1 – REFLECTIVE (why the user got it wrong):
 * - Cards are fixed reflective options only (no keyword interpolation):
 *   EN: "I guessed", "I had no idea"; NL: "Ik gokte", "Ik had geen idee"; AR: equivalents.
 * - Coach response: supportive nudge only (e.g. "Consider who has priority"), never the answer.
 * - Same logic for every practice test and question type (theory, road signs, hazard, etc.).
 *
 * ROUND 2 – FACTUAL (clarification per question):
 * - Prompts and responses are derived from this question’s context and explanation.
 * - Round 2 prompts differ per question (not the same "When is it allowed?" for every question).
 * - Responses are strict factual sentences from the explanation (≤18 words).
 *
 * RULES:
 * - Cards ≤6 words; coach responses short; question-specific where possible.
 * - No generic repeated follow-ups across questions or tests.
 * - No follow-ups must appear after the move-on message (chips cleared before move-on; UI hides suggestions when coaching complete).
 */

import { generatePerQuestionCoaching, getMoveOnMessage } from './questionCoaching.perQuestion';
import type { Language, QuestionCoaching } from './questionCoaching.types';

export { getMoveOnMessage };

const RULE_KEYWORDS: Record<Language, string[]> = {
  en: ['must', 'should', 'always', 'never', 'only', 'stop', 'yield', 'right of way', 'proceed'],
  nl: ['moet', 'mag', 'altijd', 'nooit', 'alleen', 'stop', 'voorrang', 'doorgaan'],
  ar: ['يجب', 'لا', 'فقط', 'توقف', 'أولوية', 'تابع']
};

const splitSentences = (text: string): string[] => {
  const clean = (text || '').replace(/\s+/g, ' ').trim();
  if (!clean) return [];
  return clean.split(/[.!?؟]/).map(s => s.trim()).filter(Boolean);
};

const normalizeSentence = (text: string): string => {
  return (text || '').toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, '').replace(/\s+/g, ' ').trim();
};

const findRuleSentence = (explanation: string, language: Language): string => {
  const sentences = splitSentences(explanation);
  if (sentences.length === 0) return '';
  const keywords = RULE_KEYWORDS[language];
  const match = sentences.find(sentence =>
    keywords.some(keyword => sentence.toLowerCase().includes(keyword))
  );
  return match || '';
};

const isStrictRound2Sentence = (response: string, sentences: string[], maxWords: number): boolean => {
  if (!response) return false;
  const wordCount = response.split(/\s+/).filter(Boolean).length;
  if (wordCount > maxWords) return false;
  const normalized = normalizeSentence(response);
  return sentences.some(sentence => normalizeSentence(sentence) === normalized);
};

const enforceStrictFollowups = (
  coaching: QuestionCoaching,
  explanation: string,
  language: Language
): QuestionCoaching => {
  const sentences = splitSentences(explanation);
  const maxRound2Words = 18;
  const ruleSentence = findRuleSentence(explanation, language);
  const normalizedRule = normalizeSentence(ruleSentence);
  const normalizedTakeHome = normalizeSentence(coaching.takeHome);
  const strictTakeHomeOk =
    normalizedRule && normalizedTakeHome && normalizedRule.startsWith(normalizedTakeHome);
  // Always show a take-home after Round 1: use coaching.takeHome when validated, else keep it or fallback
  const fallbackRule = (ruleSentence && ruleSentence.trim()) || (sentences[0] && sentences[0].trim()) || '';
  const takeHome =
    strictTakeHomeOk
      ? coaching.takeHome
      : (coaching.takeHome?.trim() || '').length > 0
        ? coaching.takeHome
        : fallbackRule;

  let round2 = coaching.round2;
  if (round2 && sentences.length > 0) {
    const card1Ok = isStrictRound2Sentence(round2.card1.coach, sentences, maxRound2Words);
    const card2Ok = isStrictRound2Sentence(round2.card2.coach, sentences, maxRound2Words);
    if (!card1Ok || !card2Ok) {
      round2 = undefined;
    }
  } else {
    round2 = undefined;
  }

  return { ...coaching, takeHome, round2 };
};

export type { Language, QuestionCoaching } from './questionCoaching.types';

/**
 * Generate deterministic coaching for a question
 * No AI calls - pure logic based on question content
 * 
 * RULES:
 * - Cards must be SHORT (≤6 words)
 * - Coach responses must be SHORT (≤10 words)
 * - All cards must be LOGICAL and RELEVANT to the question topic
 * - NO nonsense or extracted random words
 */
export function generateQuestionCoaching(
  questionId: string,
  questionText: string,
  subject: string,
  correctAnswer: string,
  explanation: string,
  language: Language = 'en',
  questionIndex?: number
): QuestionCoaching {
  const _subjectLower = subject.toLowerCase();
  const _questionLower = questionText.toLowerCase();
  
  // Extract SHORT take-home rule (8-10 words max) - accurate but concise
  // Key rule in explanation shows full sentence, take-home is shortened for voice
  const extractShortRule = (text: string): string => {
    if (!text) return '';
    const clean = text.replace(/\s+/g, ' ').trim();
    const sentences = clean.split(/[.!?؟]/).map(s => s.trim()).filter(Boolean);
    const ruleKeywords = [
      'must', 'should', 'always', 'never', 'only', 'stop', 'yield', 'right of way', 'proceed',
      'moet', 'mag', 'altijd', 'nooit', 'alleen', 'stop', 'voorrang', 'doorgaan',
      'يجب', 'لا', 'فقط', 'توقف', 'أولوية', 'تابع'
    ];
    const isRuleLike = (sentence: string) => {
      const lower = sentence.toLowerCase();
      return ruleKeywords.some(keyword => lower.includes(keyword));
    };
    const chosenSentence = sentences.find(isRuleLike) || sentences[0] || clean;
    const firstSentence = chosenSentence;
    
    // Shorten to ~8 words while keeping accuracy
    const words = firstSentence.split(/\s+/).filter(Boolean);
    const hasRuleKeyword = isRuleLike(firstSentence);
    const maxWords = hasRuleKeyword ? 14 : 8;
    if (words.length <= maxWords) {
      return firstSentence;
    }
    
    // Take first N words, clean ending, add period
    let shortened = words.slice(0, maxWords).join(' ');
    // Remove trailing conjunctions/prepositions
    shortened = shortened.replace(/\s+(and|or|the|a|an|if|unless|that|which|when|where|but|so|to|for|of|in|on|at|by|with)$/i, '');
    // Ensure ends with period
    if (!shortened.match(/[.!?]$/)) {
      shortened += '.';
    }
    return shortened;
  };
  
  const takeHomeRule = extractShortRule(explanation);
  
  // All tests (including TLS) use same reflective Round 1 + motivational move-on
  const coaching = generatePerQuestionCoaching({
    questionId,
    questionText,
    subject,
    correctAnswer,
    explanation,
    takeHomeRule,
    language,
    questionIndex
  });
  return enforceStrictFollowups(coaching, explanation, language);
}



/**
 * Get cards for a specific round
 */
export function getCardsForRound(coaching: QuestionCoaching, round: 1 | 2): string[] {
  if (round === 1) {
    return [coaching.round1.card1.prompt, coaching.round1.card2.prompt];
  }
  if (round === 2 && coaching.round2) {
    return [coaching.round2.card1.prompt, coaching.round2.card2.prompt];
  }
  return [];
}

/**
 * Get coach response for a card prompt
 */
export function getCoachResponse(coaching: QuestionCoaching, cardPrompt: string, round: 1 | 2): string {
  const promptLower = cardPrompt.toLowerCase().trim();
  
  // Check round 1
  if (round === 1) {
    if (coaching.round1.card1.prompt.toLowerCase().includes(promptLower) ||
        promptLower.includes(coaching.round1.card1.prompt.toLowerCase())) {
      return coaching.round1.card1.coach;
    }
    if (coaching.round1.card2.prompt.toLowerCase().includes(promptLower) ||
        promptLower.includes(coaching.round1.card2.prompt.toLowerCase())) {
      return coaching.round1.card2.coach;
    }
    // Fallback to first card response
    return coaching.round1.card1.coach;
  }
  
  // Check round 2
  if (round === 2 && coaching.round2) {
    if (coaching.round2.card1.prompt.toLowerCase().includes(promptLower) ||
        promptLower.includes(coaching.round2.card1.prompt.toLowerCase())) {
      return coaching.round2.card1.coach;
    }
    if (coaching.round2.card2.prompt.toLowerCase().includes(promptLower) ||
        promptLower.includes(coaching.round2.card2.prompt.toLowerCase())) {
      return coaching.round2.card2.coach;
    }
    // Fallback to first card response
    return coaching.round2.card1.coach;
  }
  
  // No round 2, return take-home
  return coaching.takeHome;
}

/**
 * Check if there's a second round available
 */
export function hasRound2(coaching: QuestionCoaching): boolean {
  return !!coaching.round2;
}

// Cache for generated coaching (avoid regenerating)
// CRITICAL: Cache is per-question, cleared when new question starts
const coachingCache = new Map<string, QuestionCoaching>();

/**
 * Get or generate coaching for a question (with caching)
 * Uses questionId + questionIndex so each question position gets different follow-ups (avoids same prompts when IDs repeat).
 */
export function getQuestionCoaching(
  questionId: string,
  questionText: string,
  subject: string,
  correctAnswer: string,
  explanation: string,
  language: Language = 'en',
  questionIndex?: number
): QuestionCoaching {
  const index = questionIndex ?? 0;
  const cacheKey = `${questionId}-${language}-${index}`;

  if (coachingCache.has(cacheKey)) {
    return coachingCache.get(cacheKey)!;
  }

  const coaching = generateQuestionCoaching(
    questionId,
    questionText,
    subject,
    correctAnswer,
    explanation,
    language,
    questionIndex
  );
  coachingCache.set(cacheKey, coaching);
  return coaching;
}

/**
 * Clear all coaching cache (call when starting a new practice test so follow-ups are fresh).
 */
export function clearCoachingCache(): void {
  coachingCache.clear();
}